<template>
  <div class="geofence-map-wrap">
    <!-- Location search: only pans/zooms the map view; never sets geometry. -->
    <input
      v-show="showSearch"
      ref="searchInput"
      type="text"
      class="map-search"
      :placeholder="$t('reports.cropProgression.searchLocation')"
      @keydown.enter.prevent
    />
    <div class="map-toolbar" v-if="source === 'polygon' || source === 'circle'">
      <v-btn small text color="primary" @click="clearDrawing">
        <v-icon left small>mdi-delete-outline</v-icon>
        {{ $t("reports.cropProgression.clearDrawing") }}
      </v-btn>
    </div>
    <div ref="googleMap" class="geofence-map"></div>
  </div>
</template>

<script>
import { getGoogleMapsLoader } from "@/mixins/GoogleMapLoaderSingleton";

const DEFAULT_CENTER = { lat: -12.3788, lng: -46.6621 };
const MIN_CIRCLE_RADIUS_M = 1; // circle grows from the center as the cursor drags

export default {
  name: "GeofenceMap",
  props: {
    // 'farm_zone' | 'polygon' | 'circle' | 'geojson'
    source: { type: String, default: "polygon" },
    // farm-zone object (from FarmZoneSelect) for read-only outline
    zone: { type: Object, default: null },
    // parsed polygon coords [{lat,lng}] from an uploaded GeoJSON
    geojsonCoordinates: { type: Array, default: () => [] },
    // circle radius in METERS, two-way synced with the radius input field
    radiusMeters: { type: Number, default: null },
  },
  data() {
    return {
      google: null,
      map: null,
      polygon: null,
      circle: null,
      polygonCoords: [],
      placeAutocomplete: null,
      // in-progress polygon drawing
      vertexMarkers: [],
      drawPolyline: null,
      drawingClosed: false,
    };
  },
  computed: {
    // Location search is offered only in the drawing modes.
    showSearch() {
      return this.source === "polygon" || this.source === "circle";
    },
  },
  watch: {
    source() {
      this.applySource();
    },
    zone() {
      if (this.source === "farm_zone") this.applySource();
    },
    geojsonCoordinates() {
      if (this.source === "geojson") this.applySource();
    },
    // Radius input field -> map circle. Epsilon guard avoids a feedback loop
    // with the radius_changed listener that mirrors map -> field.
    radiusMeters(val) {
      if (!this.circle || !val || val <= 0) return;
      if (Math.abs(this.circle.getRadius() - val) >= 0.5) {
        this.circle.setRadius(val);
      }
    },
  },
  async mounted() {
    try {
      const loader = getGoogleMapsLoader();
      this.google = await loader.load();
      this.map = new this.google.maps.Map(this.$refs.googleMap, {
        center: DEFAULT_CENTER,
        zoom: 5,
        mapTypeId: "satellite",
        tilt: 0,
        streetViewControl: false,
        fullscreenControl: false,
        mapTypeControl: false,
      });
      this.applySource();
      this.setupSearch();
    } catch (e) {
      console.error("GeofenceMap init error:", e);
    }
  },
  beforeDestroy() {
    this.clearAll();
  },
  methods: {
    clearAll() {
      if (this.polygon) {
        this.polygon.setMap(null);
        this.polygon = null;
      }
      if (this.circle) {
        this.circle.setMap(null);
        this.circle = null;
      }
      // in-progress polygon drawing artifacts
      this.vertexMarkers.forEach((m) => m.setMap(null));
      this.vertexMarkers = [];
      if (this.drawPolyline) {
        this.drawPolyline.setMap(null);
        this.drawPolyline = null;
      }
      this.drawingClosed = false;
      if (this.map && this.google) {
        this.google.maps.event.clearListeners(this.map, "click");
      }
    },
    clearDrawing() {
      this.clearAll();
      this.polygonCoords = [];
      this.$emit("update:geometry", null);
      this.applySource();
    },

    /**
     * Google Places search that only recenters/zooms the map — it never creates
     * geometry or sets coordinates. The input stays mounted (v-show) so we bind
     * the Autocomplete once.
     */
    setupSearch() {
      const input = this.$refs.searchInput;
      if (!input || !this.google || !this.google.maps.places) return;
      const ac = new this.google.maps.places.Autocomplete(input, {
        fields: ["geometry"],
      });
      ac.addListener("place_changed", () => {
        const place = ac.getPlace();
        if (!place || !place.geometry) return;
        if (place.geometry.viewport) {
          this.map.fitBounds(place.geometry.viewport);
        } else if (place.geometry.location) {
          this.map.setCenter(place.geometry.location);
          this.map.setZoom(16);
        }
      });
      this.placeAutocomplete = ac;
    },
    applySource() {
      if (!this.map || !this.google) return;
      this.clearAll();
      switch (this.source) {
        case "farm_zone":
          this.drawZoneOutline();
          break;
        case "geojson":
          this.drawGeojsonOutline();
          break;
        case "circle":
          this.setupCircleDraw();
          break;
        case "polygon":
        default:
          this.setupPolygonDraw();
          break;
      }
    },

    /* ---------- read-only outlines ---------- */
    drawZoneOutline() {
      const zone = this.zone;
      if (!zone) return;
      const radiusM = Number(zone.geofenceRadius);
      const centerLat = Number(zone.geofenceCenterLat);
      const centerLng = Number(zone.geofenceCenterLog);
      if (radiusM > 0 && !isNaN(centerLat) && !isNaN(centerLng)) {
        this.circle = new this.google.maps.Circle({
          center: { lat: centerLat, lng: centerLng },
          radius: radiusM,
          strokeColor: "#2196F3",
          strokeWeight: 2,
          fillColor: "#2196F3",
          fillOpacity: 0.1,
          editable: false,
          map: this.map,
        });
        this.map.fitBounds(this.circle.getBounds());
        return;
      }
      const coords = this.zoneToLatLng(zone.geofence_coordinates || zone.coordinates || []);
      if (coords.length >= 3) {
        this.polygon = new this.google.maps.Polygon({
          paths: coords,
          strokeColor: "#2196F3",
          strokeWeight: 2,
          fillColor: "#2196F3",
          fillOpacity: 0.1,
          editable: false,
          map: this.map,
        });
        this.fitToCoords(coords);
      }
    },
    zoneToLatLng(list) {
      return (list || [])
        .map((c) => ({
          lat: Number(c.lat != null ? c.lat : c.latitude),
          lng: Number(c.lng != null ? c.lng : c.log != null ? c.log : c.longitude),
        }))
        .filter((c) => !isNaN(c.lat) && !isNaN(c.lng));
    },
    drawGeojsonOutline() {
      const coords = (this.geojsonCoordinates || []).filter(
        (c) => c && !isNaN(c.lat) && !isNaN(c.lng)
      );
      if (coords.length < 3) return;
      this.polygon = new this.google.maps.Polygon({
        paths: coords,
        strokeColor: "#FF6B00",
        strokeWeight: 2,
        fillColor: "#FF6B00",
        fillOpacity: 0.15,
        editable: false,
        map: this.map,
      });
      this.fitToCoords(coords);
      this.emitPolygon(coords);
    },

    /* ---------- polygon drawing (geojson.io style) ---------- */
    setupPolygonDraw() {
      this.polygonCoords = [];
      this.drawingClosed = false;
      // Each click drops a vertex dot; the ring is only finalized (and emitted)
      // when the user clicks the first vertex again to close it.
      this.map.addListener("click", (e) => {
        if (this.drawingClosed) return;
        this.addVertex(e.latLng.toJSON());
      });
    },
    vertexIcon(isFirst) {
      return {
        path: this.google.maps.SymbolPath.CIRCLE,
        scale: isFirst ? 7 : 5,
        fillColor: "#ffffff",
        fillOpacity: 1,
        strokeColor: "#FF6B00",
        strokeWeight: isFirst ? 3 : 2,
      };
    },
    addVertex(latLng) {
      this.polygonCoords.push(latLng);
      const isFirst = this.polygonCoords.length === 1;
      const marker = new this.google.maps.Marker({
        position: latLng,
        map: this.map,
        icon: this.vertexIcon(isFirst),
        cursor: isFirst ? "pointer" : undefined,
        title: isFirst ? this.$t("reports.cropProgression.closePolygonHint") : undefined,
        zIndex: isFirst ? 3 : 2,
      });
      if (isFirst) {
        // Clicking the first vertex closes the ring (needs >= 3 points).
        marker.addListener("click", () => this.closePolygon());
      }
      this.vertexMarkers.push(marker);
      // rubber-band polyline through the placed vertices
      if (!this.drawPolyline) {
        this.drawPolyline = new this.google.maps.Polyline({
          path: this.polygonCoords,
          strokeColor: "#FF6B00",
          strokeWeight: 2,
          map: this.map,
        });
      } else {
        this.drawPolyline.setPath(this.polygonCoords);
      }
      // Do NOT emit while drawing — dates fetch only after the ring is closed.
    },
    closePolygon() {
      if (this.drawingClosed || this.polygonCoords.length < 3) return;
      this.drawingClosed = true;
      const coords = this.polygonCoords.slice();
      // remove in-progress markers + polyline, replace with the final polygon
      this.vertexMarkers.forEach((m) => m.setMap(null));
      this.vertexMarkers = [];
      if (this.drawPolyline) {
        this.drawPolyline.setMap(null);
        this.drawPolyline = null;
      }
      this.polygon = new this.google.maps.Polygon({
        paths: coords,
        strokeColor: "#FF6B00",
        strokeWeight: 3,
        fillColor: "#FF6B00",
        fillOpacity: 0.2,
        editable: true,
        draggable: true,
        map: this.map,
      });
      const path = this.polygon.getPath();
      ["set_at", "insert_at", "remove_at"].forEach((ev) =>
        path.addListener(ev, () => this.updatePolygonFromPath())
      );
      this.emitPolygon(coords);
    },
    updatePolygonFromPath() {
      const path = this.polygon.getPath();
      const coords = [];
      path.forEach((latLng) => coords.push(latLng.toJSON()));
      this.polygonCoords = coords;
      this.emitPolygon(coords);
    },
    emitPolygon(coords) {
      if (!coords || coords.length < 3) {
        this.$emit("update:geometry", null);
        return;
      }
      this.$emit("update:geometry", {
        shape: "polygon",
        coordinates: coords.map((c) => ({ lat: c.lat, lng: c.lng })),
      });
    },

    /* ---------- circle drawing (single click sets center) ---------- */
    setupCircleDraw() {
      // One click sets the center; the radius comes from the field (default) and
      // can be adjusted afterwards via the field or by dragging on the map.
      this.map.addListener("click", (e) => this.placeCircle(e.latLng));
    },
    placeCircle(latLng) {
      const radius =
        this.radiusMeters && this.radiusMeters > 0 ? this.radiusMeters : MIN_CIRCLE_RADIUS_M;
      if (this.circle) {
        // Circle already placed — a further click just repositions the center.
        this.circle.setCenter(latLng);
        return;
      }
      this.circle = new this.google.maps.Circle({
        center: latLng,
        radius,
        strokeColor: "#FF6B00",
        strokeWeight: 3,
        fillColor: "#FF6B00",
        fillOpacity: 0.2,
        editable: true,
        draggable: true,
        map: this.map,
      });
      this.circle.addListener("radius_changed", () => this.emitCircle());
      this.circle.addListener("center_changed", () => this.emitCircle());
      this.emitCircle();
    },
    emitCircle() {
      if (!this.circle) return;
      const c = this.circle.getCenter();
      this.$emit("update:geometry", {
        shape: "circle",
        latitude: c.lat(),
        longitude: c.lng(),
        radius: this.circle.getRadius() / 1000, // meters -> km
      });
    },

    /* ---------- helpers ---------- */
    fitToCoords(coords) {
      const bounds = new this.google.maps.LatLngBounds();
      coords.forEach((c) => bounds.extend(new this.google.maps.LatLng(c.lat, c.lng)));
      this.map.fitBounds(bounds);
    },
  },
};
</script>

<style scoped>
.geofence-map-wrap {
  position: relative;
  width: 100%;
}
.geofence-map {
  width: 100%;
  height: 520px;
  border-radius: 6px;
}
.map-toolbar {
  position: absolute;
  z-index: 2;
  top: 8px;
  right: 8px;
  background: rgba(255, 255, 255, 0.92);
  border-radius: 4px;
}
.map-search {
  position: absolute;
  z-index: 3;
  top: 10px;
  left: 10px;
  width: 280px;
  max-width: 60%;
  padding: 9px 12px;
  border: 1px solid #d5d5d5;
  border-radius: 4px;
  background: #fff;
  font-size: 14px;
  outline: none;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
}
</style>
